import { useEffect, useRef, useState } from 'react';
import { weatherApi, type DayHours } from '../api/weather.api';

interface HourlyWeather {
  byDate: Record<string, DayHours>;
  loading: boolean;
}

/**
 * The hour-by-hour trace of a trip, keyed by `YYYY-MM-DD`.
 *
 * Separate from `useTripWeather` and gated behind `enabled` on purpose: the
 * trace is an order of magnitude heavier than the day summary and only the
 * weather panel wants it, so nobody pays for it by opening the itinerary. Once
 * fetched it stays — reopening the panel costs nothing, until `placesKey`
 * (`tripWeatherKey`) changes.
 *
 * `null` is "not answered yet" and `{}` is "answered with nothing", which is
 * what lets `loading` be derived instead of tracked in a second state. A failure
 * lands in the second case: no weather is a valid state, not an error to raise.
 */
export function useTripHourlyWeather(tripId: string, placesKey: string, enabled: boolean): HourlyWeather {
  const [byDate, setByDate] = useState<Record<string, DayHours> | null>(null);
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    const key = `${tripId}|${placesKey}`;
    if (!enabled || fetchedFor.current === key) return;
    fetchedFor.current = key;
    setByDate(null);

    let cancelled = false;
    let landed = false;
    weatherApi.hourly(tripId)
      .then(days => {
        if (!cancelled) setByDate(Object.fromEntries(days.map(d => [d.date, d])));
      })
      .catch(() => {
        if (!cancelled) setByDate({}); // the panel falls back to the day summary
      })
      .finally(() => { landed = true; });

    return () => {
      cancelled = true;
      // A request abandoned before it answered leaves nothing behind, so the
      // guard has to forget it as well. Without this, closing the panel inside
      // the fetch window strands it on the spinner until a reload — and in dev
      // it never loads at all, because StrictMode's second mount finds the trip
      // already "fetched" and the first mount's answer was thrown away.
      if (!landed) fetchedFor.current = null;
    };
  }, [tripId, placesKey, enabled]);

  return { byDate: byDate ?? {}, loading: enabled && byDate === null };
}
